'use client';
import {
  Table as MuiTable,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  TablePagination,
  TableSortLabel,
  TableCellProps,
  Tooltip,
} from '@mui/material';
import { ReactNode, useState } from 'react';

type SortOrder = 'asc' | 'desc';

export interface SortConfig {
  field: string;
  order: SortOrder;
}

export interface TableItemProps extends TableCellProps {
  item: ReactNode | string;
  colSpan?: number;
  sortValue?: number | string;
  showItem?: boolean;
  order?: number;
  id?: string;
}

export interface TableRowProps {
  name?: string;
  id: string;
  isSelected?: boolean;
  nameColSpan?: number;
  items?: TableItemProps[];
  disabled?: boolean;
}

export interface HeaderCellProps extends TableCellProps {
  label: ReactNode | string;
  colSpan?: number;
  sortable?: boolean;
  sortKey?: string;
  showItem?: boolean;
  order?: number;
  id?: string;
}

export interface TableProps {
  headerRows?: HeaderCellProps[] | string[];
  data?: TableRowProps[];
  loading?: boolean;
  name?: string;
  height?: number | string;
  width?: number | string;
  maxHeight?: number | string;
  noDataMessage?: string;
  error?: string;
  enablePagination?: boolean;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  TablePaginationActions?: any;
  enableSorting?: boolean;
  defaultSortConfig?: SortConfig;
  stickyHeader?: boolean;
}

export function Table({
  headerRows,
  data,
  loading,
  height = 200,
  width = '100%',
  maxHeight,
  name,
  noDataMessage,
  error,
  enablePagination = false,
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 5,
  TablePaginationActions,
  enableSorting = false,
  defaultSortConfig,
  stickyHeader = false,
}: TableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(defaultSortConfig);

  if (loading) {
    return (
      <Skeleton
        width={width}
        height={height}
        sx={{ transform: 'unset' }}
        aria-label={`${name} table loading`}
      />
    );
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    const newOrder: SortOrder =
      sortConfig?.field === field && sortConfig.order === 'asc' ? 'desc' : 'asc';

    const newSortConfig: SortConfig = { field, order: newOrder };
    setSortConfig(newSortConfig);
  };

  const getHeaderIndex = (field: string): number => {
    if (!headerRows) return -1;
    return headerRows.findIndex(header =>
      typeof header === 'string' ? header === field : header.sortKey === field
    );
  };

  const sortedData =
    enableSorting && data && sortConfig
      ? [...data].sort((a, b) => {
          const headerIndex = getHeaderIndex(sortConfig.field);
          if (headerIndex === -1) return 0;

          let aField, bField;
          if (sortConfig.field === 'name' && a.name && b.name) {
            aField = a.name;
            bField = b.name;
          } else if (a.items && b.items) {
            const offsetIndex = a.name ? headerIndex - 1 : headerIndex;
            aField = a.items[offsetIndex].sortValue
              ? a.items[offsetIndex].sortValue
              : a.items[offsetIndex].item;
            bField = b.items[offsetIndex].sortValue
              ? b.items[offsetIndex].sortValue
              : b.items[offsetIndex].item;
          }

          const comparison = (() => {
            if (aField === undefined && bField === undefined) return 0;
            if (aField === undefined) return 1;
            if (bField === undefined) return -1;
            if (typeof aField === 'number' && typeof bField === 'number') {
              return aField - bField;
            }
            return String(aField).localeCompare(String(bField));
          })();

          return sortConfig.order === 'asc' ? comparison : -comparison;
        })
      : data;

  const paginatedData =
    enablePagination && sortedData
      ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedData;

  // Helper function to process header cell
  const renderHeaderCell = (header: HeaderCellProps | string, index: number) => {
    if (typeof header === 'string') {
      return <TableCell key={`header-${index}`}>{header}</TableCell>;
    }

    const { showItem = true } = header;
    if (!showItem) return null;

    const content =
      enableSorting && header.sortable && header.sortKey ? (
        <TableSortLabel
          active={sortConfig?.field === header.sortKey}
          direction={sortConfig?.field === header.sortKey ? sortConfig.order : 'asc'}
          onClick={() => handleSort(header.sortKey!)}
        >
          {header.label}
        </TableSortLabel>
      ) : (
        header.label
      );

    return (
      <TableCell key={`header-${index}`} colSpan={header.colSpan} size={header.size}>
        {content}
      </TableCell>
    );
  };
  const getVisibleHeaderCount = () => {
    if (!headerRows) return 0;
    return headerRows.filter(header => {
      if (typeof header === 'string') return true;
      return header.showItem !== false;
    }).length;
  };
  return (
    <>
      <TableContainer sx={{ width: width, maxHeight: maxHeight }}>
        <MuiTable aria-label={name} stickyHeader={stickyHeader}>
          {headerRows && (
            <TableHead>
              <TableRow>
                {headerRows.map((header, index) => renderHeaderCell(header, index))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {error ? (
              <TableRow>
                <TableCell colSpan={getVisibleHeaderCount()} align="center">
                  {error}
                </TableCell>
              </TableRow>
            ) : !paginatedData || paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={getVisibleHeaderCount()} align="center">
                  {noDataMessage || 'No data available'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData?.map(({ id, name, nameColSpan, items, isSelected, disabled }) => (
                <Tooltip
                  key={id}
                  title={disabled ? 'Data Analysis in Progress.' : ''}
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -50],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <TableRow
                    key={id}
                    data-testid={id}
                    selected={isSelected}
                    className={disabled ? 'disabled' : ''}
                  >
                    {name && (
                      <TableCell component="th" scope="row" colSpan={nameColSpan}>
                        {name}
                      </TableCell>
                    )}
                    {items?.map(
                      ({ item, colSpan, size, showItem = true }, index) =>
                        showItem && (
                          <TableCell key={`cell-${index}`} colSpan={colSpan} size={size}>
                            {item}
                          </TableCell>
                        )
                    )}
                  </TableRow>
                </Tooltip>
              ))
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {enablePagination && data && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      )}
    </>
  );
}

export default Table;
